import axios from 'axios';
import { useEffect, useState } from 'react';
import { baseUrl, getCurrentUser } from '../../../util/host';

export const getSupplierApiBase = () => baseUrl('REACT_APP_SUPPLIER_API_URL');

export const SUPPLIER_LIST_PATH = '/sp/supplier/list';

const supplierHeaders = () => {
  const token = getCurrentUser()?.token || '';
  return {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
    x_styliplm_internal_header: 'keystylirsf87hgdg57bh6dc65ch',
    token: `Bearer ${token}`,
  };
};

const extractSupplierRows = (body) => {
  const payload = body?.resposne || body?.response;
  if (Array.isArray(payload?.supplier)) return payload.supplier;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.result)) return body.result;
  return [];
};

/**
 * Deduplicates suppliers on seller_id and returns them sorted by company_name.
 * Result shape: [{ id, label }] where id is seller_id and label is company_name.
 */
const uniqueSortedSuppliers = (rows) => {
  const byId = new Map();
  rows.forEach((row) => {
    const id = row?.seller_id;
    const label = (row?.company_name || '').trim();
    if (id == null || !label) return;
    if (!byId.has(String(id))) byId.set(String(id), { id, label });
  });
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
};

/** Fetches all suppliers from the apigee list endpoint. */
const fetchAllSuppliers = async () => {
  const base = getSupplierApiBase();
  if (!base) {
    console.warn(
      '[supplierApiHelpers] REACT_APP_SUPPLIER_API_URL is not configured; '
      + 'skipping supplier fetch. Restart the dev server after updating the env file.'
    );
    return [];
  }
  const url = `${base}${SUPPLIER_LIST_PATH}`;
  const headers = supplierHeaders();
  console.info('[supplierApiHelpers] fetching supplier list', url);

  const res = await axios.post(url, { noLimit: true }, { headers });
  const suppliers = uniqueSortedSuppliers(extractSupplierRows(res?.data));
  console.info(`[supplierApiHelpers] loaded ${suppliers.length} suppliers`);
  return suppliers;
};

let supplierCache = null;
let supplierPromise = null;

export const getAllSuppliers = () => {
  if (supplierCache) return Promise.resolve(supplierCache);
  if (!supplierPromise) {
    supplierPromise = fetchAllSuppliers()
      .then((list) => {
        supplierCache = list;
        return list;
      })
      .catch((err) => {
        // Reset so subsequent mounts can retry instead of hanging forever.
        supplierPromise = null;
        throw err;
      });
  }
  return supplierPromise;
};

/** Hook that loads the shared supplier list once per app session. */
export const useSuppliers = () => {
  const [suppliers, setSuppliers] = useState(() => supplierCache || []);
  const [loading, setLoading] = useState(() => !supplierCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (supplierCache) return undefined;
    let alive = true;
    setLoading(true);
    getAllSuppliers()
      .then((list) => {
        if (!alive) return;
        setSuppliers(list);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('Failed to load supplier list', err);
        setError(err);
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  return { suppliers, loading, error };
};
